import { jsx } from 'react/jsx-runtime';
import { forwardRef, useLayoutEffect } from 'react';
import clsx from 'clsx';
import { getCanIncrement, isIncrementFormatter } from '../formatter/formatter.utils.js';
import { useMergeRefs } from '../hooks/use-merge-refs.js';
import { Input } from '../input/input.js';
import { Root as Root$1, useScrubbable, Trigger as Trigger$1 } from '../scrubbable/scrubbable.js';
import { useScrubbableInput } from './hooks/use-formatted-input.js';
import { ScrubbableInputContext, useScrubbableInputContext, useSetScrubbableInputContext } from './scrubbable-input.context.js';
import { Stepper as Stepper$1 } from './stepper.js';
import { trigger, icon } from './scrubbable-input.css.js';

/**
 * An input that is configured with a formatter object. The input converts from the type the
 * formatter is configured to accept (e.g. a number) to a string for display, and back again
 * on change. Formatted inputs are also set up to handle up and down arrow keys to increment
 * and decrement the value.
 *
 * By default, ScrubbableInput is semi-controlled, meaning it does not call onChange as the user
 * types, but instead waits for the user to blur the input or hit enter. For up/down arrow keys,
 * onChange is called immediately for every keystroke. For scrubbing, onChange is called on every
 * pointermove event with `commit: false`, and then once more on pointerup with `commit: true`.
 *
 * ScrubbableInput should always contain either `ScrubbableInput.Trigger` or `ScrubbableInput.Icon`
 * in order to be scrubbable somewhere with a pointer and no modifier keys. If there is no icon,
 * an empty `ScrubbableInput.Trigger` can be placed as the first child to make the first 8px of the
 * input scrubbable.
 *
 * @compose { "root": true, "strictChildren": true }
 * @see https://fpl.figma.design/components/inputs/scrubbable-input/
 */
const Root = forwardRef((props, ref) => {
    return (jsx(ScrubbableInputContext, { children: jsx(RootInner, { ...props, forwardedRef: ref }) }));
});
Root.displayName = 'ScrubbableInput.Root';
// eslint-disable-next-line react/display-name
function RootInner({ forwardedRef, ...props }) {
    const ctx = useScrubbableInputContext();
    return (jsx(Root$1, { "data-fpl-component": "", manager: ctx?.manager ?? null, children: jsx(Input.Root, { ref: forwardedRef, ...props }) }));
}
const _Field = forwardRef(({ onScrubScaleChange, ...props }, refIn) => {
    const setCtx = useSetScrubbableInputContext();
    const ctx = useScrubbableInput(props);
    const manager = useScrubbable({
        value: ctx.value,
        getStringValue: ctx.getStringValue,
        formatter: ctx.formatter,
        disabled: props.disabled || !getCanIncrement('scrub', ctx.formatter, ctx.value),
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        onChange: (value, { commit }) => ctx.onChange(value, { commit, source: 'scrub' }),
        onChangeRestricted: props.onChangeRestricted,
        // TODO: what to do onCancel? don't want to pass In because onChange expects Out
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        onCancel: (value) => ctx.onChange(value, { commit: true, source: 'scrub' }),
        onScrubScaleChange,
    });
    // The `setCtx` function comes from useState, which is stable
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useLayoutEffect(() => setCtx({ ctx, manager }), [ctx, manager]);
    const { inputProps } = ctx;
    const ref = useMergeRefs(refIn, inputProps.ref);
    return (jsx(Input, { "data-fpl-component": "", ...inputProps, ref: ref, "data-faux-focus": manager.scrubbing || undefined }));
});
_Field.displayName = 'ScrubbableInput.Field';
/**
 * The typeable portion of the formatted input. Must be a direct child of `FormattedInput.Root`.
 *
 * @compose { "parent": ["ScrubbableInput.Root"] }
 */
const Field = _Field;
/**
 * An explicit area within the input that acts as the scrub trigger. If omitted, scrubbing would
 * require holding alt while hovering the input. Children are optional, and when omitted the
 * trigger will be an 8px wide empty space.
 *
 * @compose { "parent": ["ScrubbableInput.Root"] }
 */
const Trigger = forwardRef(({ children, className, style, ...props }, ref) => (jsx(Trigger$1, { "data-fpl-component": "", ...props, ref: ref, className: clsx(trigger, className), onPointerDown: (e) => {
        e.preventDefault();
        props.onPointerDown?.(e);
    }, children: children })));
Trigger.displayName = 'ScrubbableInput.Trigger';
/**
 * An icon that acts as the scrub trigger. Prefer over using ScrubbableInput.Trigger directly
 * because this uses `var(--color-icon-secondary)` as the icon color.
 *
 * @compose { "parent": ["ScrubbableInput.Root"] }
 */
const Icon = forwardRef(({ className, ...props }, ref) => (jsx(Trigger, { "data-fpl-component": "", ...props, ref: ref, className: clsx(icon, className) })));
Icon.displayName = 'ScrubbableInput.Icon';
/**
 * Place as the last child of `FormattedInput.Root` to provide up/down arrow buttons.
 *
 * @note requires this input is set up with an IncrementFormatter, otherwise doesn't render.
 * @compose { "parent": ["ScrubbableInput.Root"] }
 */
const Stepper = forwardRef((_, ref) => {
    const ctx = useScrubbableInputContext();
    if (!ctx || ctx.ctx.inputProps.disabled || !isIncrementFormatter(ctx.ctx.formatter)) {
        return null;
    }
    return (jsx(Stepper$1, { "data-fpl-component": "", ref: ref, value: ctx.ctx.value, formatter: ctx.ctx.formatter, getStringValue: ctx.ctx.getStringValue, onChange: (value) => ctx.ctx.onChange(value, { commit: true, source: 'stepper' }), onChangeRestricted: ctx.ctx.onChangeRestricted }));
});
Stepper.displayName = 'ScrubbableInput.Stepper';

export { Field, Icon, Root, Stepper, Trigger };
//# sourceMappingURL=scrubbable-input.js.map
